import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, BarChart3, Loader2, Eye, Users, Smartphone, Tablet, Monitor,
  Globe, Trash2, RefreshCcw, Download, Bell, Ban, Shirt,
} from "lucide-react";
import { Header } from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";

const RANGES = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 365, label: "Last year" },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [pushStats, setPushStats] = useState(null);
  const [lineupStats, setLineupStats] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/admin/push/stats")
      .then((r) => { if (alive) setPushStats(r.data || null); })
      .catch(() => { if (alive) setPushStats(null); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/analytics/lineup-shares?days=${days}`)
      .then((r) => { if (alive) setLineupStats(r.data || null); })
      .catch(() => { if (alive) setLineupStats(null); });
    return () => { alive = false; };
  }, [days]);

  const load = async (windowDays) => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/admin/analytics/summary?days=${windowDays}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const onClear = async () => {
    if (!window.confirm("Wipe all analytics history? This can't be undone.")) return;
    setClearing(true);
    try {
      await api.delete("/admin/analytics/clear");
      await load(days);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setClearing(false);
    }
  };

  const maxDailyViews = useMemo(() => {
    if (!data?.daily?.length) return 0;
    return Math.max(...data.daily.map((d) => d.views));
  }, [data]);

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="page-admin-analytics"
    >
      <Header />
      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8 sm:py-14">
        <RouterLink
          to="/admin"
          className="inline-flex items-center gap-1.5 label-kicker text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
        >
          <ArrowLeft size={12} /> Back to admin
        </RouterLink>

        <div className="mt-6 mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-brand-accent">
              <BarChart3 size={16} strokeWidth={2.5} />
              <span className="label-kicker">Site analytics</span>
            </div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
              How visitors are using LAST.
            </h1>
            <p className="mt-2 text-sm text-[var(--brand-body)]">
              Tracked locally on your own database. No cookies, no third parties.
              Your own admin visits are filtered out automatically.
            </p>
            <RouterLink
              to="/admin/analytics-v2"
              data-testid="analytics-v1-beta-link"
              className="mt-2 inline-block text-xs font-bold uppercase tracking-wider text-brand-accent underline-offset-2 hover:underline"
            >
              Try the new analytics (beta) — visitors / sessions / views, split apart →
            </RouterLink>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="analytics-range"
              className="rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm font-bold"
            >
              {RANGES.map((r) => (
                <option key={r.days} value={r.days}>{r.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => load(days)}
              disabled={loading}
              data-testid="analytics-refresh"
              aria-label="Refresh analytics"
              className="rounded-md border border-black/15 bg-white p-2 hover:border-[#0A0F17] disabled:opacity-50"
            >
              <RefreshCcw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="analytics-error"
          >
            {error}
          </p>
        ) : null}

        {pushStats ? (
          <div
            className="mb-6 flex flex-wrap items-center gap-3 rounded-md border border-dashed border-[#6CABDD]/50 bg-[#6CABDD]/5 px-3 py-2"
            data-testid="admin-push-stats"
          >
            <Bell size={14} strokeWidth={2.5} className="text-[var(--brand-accent)]" />
            <span className="font-display text-sm font-bold text-[var(--brand-ink)]">
              {pushStats.subscribers} device{pushStats.subscribers === 1 ? "" : "s"} with alerts on
            </span>
            {!pushStats.vapid_configured ? (
              <span className="label-kicker text-[#E63946]">
                · push keys not configured yet
              </span>
            ) : (
              <span className="label-kicker text-[var(--brand-body)]">
                · click &quot;Push&quot; on any published post to broadcast
              </span>
            )}
          </div>
        ) : null}

        {loading && !data ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-[var(--brand-body)]">
            <Loader2 size={14} className="animate-spin" /> Loading analytics…
          </div>
        ) : data ? (
          <div className="space-y-8" data-testid="analytics-dashboard">
            {/* Top stat cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Page views"
                value={data.total_views}
                hint={`Across ${data.window_days} day${data.window_days === 1 ? "" : "s"}`}
                icon={Eye}
                testid="stat-views"
              />
              <StatCard
                label="All-time page views"
                value={data.all_time?.views ?? 0}
                hint={
                  data.all_time?.since
                    ? `Since ${new Date(data.all_time.since).toLocaleDateString("en-GB", {
                        day: "numeric", month: "short", year: "numeric",
                      })}`
                    : "Lifetime total"
                }
                icon={Eye}
                testid="stat-views-all-time"
              />
              <StatCard
                label="Unique sessions"
                value={data.unique_sessions}
                hint="Distinct visitors (best-effort)"
                icon={Users}
                testid="stat-sessions"
              />
              <StatCard
                label="Today"
                value={data.unique_today}
                hint="Unique sessions so far"
                icon={Globe}
                testid="stat-today"
              />
            </div>

            {/* Daily bar chart */}
            <section className="rounded-md border border-black/10 bg-white p-5 sm:p-6">
              <h2 className="font-display text-lg font-black">Daily traffic</h2>
              {data.daily.length === 0 ? (
                <p className="mt-4 text-sm text-[var(--brand-body)]">
                  No traffic in this window yet. Once visitors browse LAST,
                  daily bars will appear here.
                </p>
              ) : (
                <div className="mt-5 flex items-end gap-1 overflow-x-auto pb-2" data-testid="analytics-daily-chart">
                  {data.daily.map((d) => {
                    const h = maxDailyViews
                      ? Math.max(4, Math.round((d.views / maxDailyViews) * 160))
                      : 4;
                    return (
                      <div
                        key={d.day}
                        className="flex shrink-0 flex-col items-center gap-1"
                        style={{ width: 30 }}
                        data-testid={`analytics-day-${d.day}`}
                        title={`${d.day}: ${d.views} views · ${d.sessions} sessions`}
                      >
                        <span className="text-[10px] font-mono text-[var(--brand-body)]">
                          {d.views}
                        </span>
                        <div
                          className="w-3 rounded-t-sm bg-[#6CABDD]"
                          style={{ height: h }}
                        />
                        <span className="rotate-45 origin-top-left text-[10px] text-[var(--brand-body)] whitespace-nowrap" style={{height: 20}}>
                          {d.day.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <ListSection
                title="Top pages"
                rows={data.top_pages}
                keyField="path"
                testid="top-pages"
                renderKey={(row) => (
                  <a
                    href={row.path}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate font-mono text-xs text-[var(--brand-ink)] underline-offset-2 hover:underline"
                  >
                    {row.path}
                  </a>
                )}
              />
              <ListSection
                title="Top referrers"
                rows={data.top_referrers}
                keyField="referrer"
                testid="top-referrers"
                renderKey={(row) => (
                  <span className="truncate font-display text-sm font-bold">
                    {row.referrer || "direct"}
                  </span>
                )}
              />
            </div>

            {/* Top countries — derived from offline GeoLite2 lookup. */}
            <CountriesSection rows={data.top_countries || []} totalViews={data.total_views} />

            {/* Excluded IPs — admin maintains a list of IPs to ignore. */}
            <ExcludedIpsSection />

            {/* Device split */}
            <section className="rounded-md border border-black/10 bg-white p-5 sm:p-6">
              <h2 className="font-display text-lg font-black">Devices</h2>
              {data.devices.length === 0 ? (
                <p className="mt-4 text-sm text-[var(--brand-body)]">No data yet.</p>
              ) : (
                <div className="mt-5 grid grid-cols-3 gap-4" data-testid="analytics-devices">
                  {data.devices.map((d) => {
                    const Icon =
                      d.device === "mobile" ? Smartphone :
                      d.device === "tablet" ? Tablet : Monitor;
                    return (
                      <div
                        key={d.device}
                        className="rounded-md border border-black/10 bg-white p-4"
                        data-testid={`device-${d.device}`}
                      >
                        <Icon size={20} strokeWidth={2.5} className="text-brand-accent" />
                        <p className="mt-2 font-display text-2xl font-black">{d.views}</p>
                        <p className="mt-1 label-kicker text-[var(--brand-body)] capitalize">
                          {d.device}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Installed app (PWA) stats */}
            <PwaSection pwa={data.pwa} windowDays={data.window_days} totalViews={data.total_views} />

            {/* Lineup Builder — builds vs shares funnel */}
            <LineupShareSection stats={lineupStats} windowDays={days} />

            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClear}
                disabled={clearing}
                data-testid="analytics-clear"
                className="inline-flex items-center gap-2 rounded-md border border-[#E63946]/30 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#E63946] hover:bg-[#E63946]/10 disabled:opacity-50"
              >
                {clearing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                {clearing ? "Wiping…" : "Wipe all analytics"}
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}

function LineupShareSection({ stats, windowDays }) {
  const builders = [
    { key: "original", label: "Lineup Builder" },
    { key: "classic", label: "Classic Lineup Builder" },
  ];
  return (
    <section
      className="rounded-md border border-black/10 bg-white p-5 sm:p-6"
      data-testid="analytics-lineup-shares"
    >
      <div className="flex items-center gap-2 text-brand-accent">
        <Shirt size={14} strokeWidth={2.5} />
        <span className="label-kicker">Lineup Builder</span>
      </div>
      <h2 className="mt-1 font-display text-lg font-black">Built vs. shared</h2>
      <p className="mt-1 text-xs text-[var(--brand-body)]">
        &quot;Built&quot; counts visits to the blank editor; &quot;Shared&quot; counts
        lineups that actually got a share link generated.
      </p>
      {!stats ? (
        <p className="mt-4 text-sm text-[var(--brand-body)]">Loading…</p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {builders.map(({ key, label }) => {
            const s = stats[key] || {};
            const conversionAllTime = s.builds_total
              ? Math.round((s.shares_total / s.builds_total) * 100)
              : 0;
            return (
              <div
                key={key}
                className="rounded-md border border-black/10 bg-[#F8FAFC] p-4"
                data-testid={`lineup-shares-${key}`}
              >
                <p className="font-display text-sm font-black">{label}</p>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <p className="font-display text-2xl font-black">{s.builds_total ?? 0}</p>
                    <p className="text-[11px] text-[var(--brand-body)]">
                      Built (all-time) · {s.builds_window ?? 0} in last {windowDays}d
                    </p>
                  </div>
                  <div>
                    <p className="font-display text-2xl font-black">{s.shares_total ?? 0}</p>
                    <p className="text-[11px] text-[var(--brand-body)]">
                      Shared (all-time) · {s.shares_window ?? 0} in last {windowDays}d
                    </p>
                  </div>
                </div>
                <p className="mt-3 text-[11px] font-bold uppercase tracking-wider text-[var(--brand-body)]">
                  {conversionAllTime}% of builders go on to share
                </p>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}


function PwaSection({ pwa, windowDays, totalViews }) {
  const safe = pwa || {
    installs_total: 0,
    installs_window: 0,
    standalone_views: 0,
    standalone_sessions: 0,
    standalone_share_pct: 0,
  };
  const hasData =
    safe.installs_total > 0 ||
    safe.installs_window > 0 ||
    safe.standalone_views > 0;
  return (
    <section
      className="rounded-md border border-black/10 bg-white p-5 sm:p-6"
      data-testid="analytics-pwa"
    >
      <div className="flex items-center gap-2 text-brand-accent">
        <Download size={14} strokeWidth={2.5} />
        <span className="label-kicker">Installed app (PWA)</span>
      </div>
      <h2 className="mt-1 font-display text-lg font-black">
        Home-screen icon usage
      </h2>
      <p className="mt-1 text-xs text-[var(--brand-body)]">
        Tracks visitors who tapped <em>Install LAST</em> and sessions that
        were launched from the installed app rather than a regular browser
        tab. iOS Add-to-Home-Screen taps don&apos;t fire an install event, but
        any iOS visit launched from the home-screen icon{" "}
        <em>does</em> count as a standalone session.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <PwaStat
          label="Installs (lifetime)"
          value={safe.installs_total}
          hint="Every accepted Install tap"
          testid="pwa-installs-total"
        />
        <PwaStat
          label={`Installs · ${windowDays}d`}
          value={safe.installs_window}
          hint="In the current window"
          testid="pwa-installs-window"
        />
        <PwaStat
          label="App-launched sessions"
          value={safe.standalone_sessions}
          hint="Visitors who opened LAST from the home-screen icon"
          testid="pwa-standalone-sessions"
        />
        <PwaStat
          label="App-launched views"
          value={safe.standalone_views}
          hint={`${safe.standalone_share_pct}% of all ${totalViews.toLocaleString("en-GB")} views`}
          testid="pwa-standalone-views"
        />
      </div>
      {!hasData ? (
        <p className="mt-4 text-xs text-[var(--brand-body)]">
          No installs yet. Once a visitor taps <em>Install LAST</em> (or adds
          the site to their iOS home screen and opens it), these numbers will
          start moving.
        </p>
      ) : null}
    </section>
  );
}

function PwaStat({ label, value, hint, testid }) {
  return (
    <div
      className="rounded-md border border-black/10 bg-[#F8FAFC] p-4"
      data-testid={testid}
    >
      <p className="label-kicker text-[var(--brand-body)]">{label}</p>
      <p className="mt-2 font-display text-2xl font-black">
        {value.toLocaleString("en-GB")}
      </p>
      <p className="mt-1 text-[11px] leading-snug text-[var(--brand-body)]">
        {hint}
      </p>
    </div>
  );
}

function StatCard({ label, value, hint, icon: Icon, testid }) {
  return (
    <div
      className="rounded-md border border-black/10 bg-white p-5"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-brand-accent">
        <Icon size={14} strokeWidth={2.5} />
        <span className="label-kicker">{label}</span>
      </div>
      <p className="mt-3 font-display text-4xl font-black tracking-tight">
        {value.toLocaleString("en-GB")}
      </p>
      <p className="mt-1 text-xs text-[var(--brand-body)]">{hint}</p>
    </div>
  );
}

function ListSection({ title, rows, testid, renderKey }) {
  const total = rows.reduce((s, r) => s + (r.views || 0), 0) || 1;
  return (
    <section className="rounded-md border border-black/10 bg-white p-5 sm:p-6">
      <h2 className="font-display text-lg font-black">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-[var(--brand-body)]">No data yet.</p>
      ) : (
        <ul className="mt-4 space-y-2" data-testid={testid}>
          {rows.map((row, i) => {
            const pct = Math.round((row.views / total) * 100);
            return (
              <li
                key={`${row.path || row.referrer || ""}-${i}`}
                className="relative overflow-hidden rounded-md border border-black/5 bg-[#F8FAFC] px-3 py-2"
              >
                <div
                  className="absolute inset-y-0 left-0 bg-[#6CABDD]/15"
                  style={{ width: `${pct}%` }}
                />
                <div className="relative flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">{renderKey(row)}</div>
                  <span className="shrink-0 font-mono text-xs text-[var(--brand-ink)]">
                    {row.views.toLocaleString("en-GB")}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ---------- Top countries (geo) ----------

// ISO-3166-1 alpha-2 → flag emoji via regional indicator symbols.
// Browsers without flag emoji fonts (Windows Chrome) will render the 2
// letters in a box, which is fine as a fallback.
function flagEmoji(iso2) {
  if (!iso2 || iso2.length !== 2) return "";
  const A = 0x1F1E6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(A + (iso2.charCodeAt(0) - a)) +
         String.fromCodePoint(A + (iso2.charCodeAt(1) - a));
}

// Minimal ISO → display-name map. Anything not in here just shows the
// code, which is still meaningful. Kept short by listing only the most
// likely countries to appear on a UK-football fan site; we can expand
// any time without touching the backend.
const COUNTRY_NAMES = {
  GB: "United Kingdom", IE: "Ireland", US: "United States", CA: "Canada",
  AU: "Australia", NZ: "New Zealand", DE: "Germany", FR: "France",
  ES: "Spain", IT: "Italy", PT: "Portugal", NL: "Netherlands",
  BE: "Belgium", SE: "Sweden", NO: "Norway", DK: "Denmark", FI: "Finland",
  PL: "Poland", CZ: "Czechia", AT: "Austria", CH: "Switzerland",
  IS: "Iceland", GR: "Greece", HU: "Hungary", RO: "Romania", BG: "Bulgaria",
  TR: "Turkey", UA: "Ukraine", RU: "Russia", IL: "Israel", AE: "U.A.E.",
  SA: "Saudi Arabia", QA: "Qatar", IN: "India", PK: "Pakistan",
  BD: "Bangladesh", LK: "Sri Lanka", SG: "Singapore", MY: "Malaysia",
  TH: "Thailand", PH: "Philippines", ID: "Indonesia", VN: "Vietnam",
  HK: "Hong Kong", TW: "Taiwan", JP: "Japan", KR: "South Korea",
  CN: "China", ZA: "South Africa", NG: "Nigeria", KE: "Kenya",
  GH: "Ghana", EG: "Egypt", MA: "Morocco", BR: "Brazil", AR: "Argentina",
  MX: "Mexico", CL: "Chile", CO: "Colombia", PE: "Peru",
};

function CountriesSection({ rows, totalViews }) {
  const tracked = rows.reduce((s, r) => s + (r.views || 0), 0);
  const max = rows.length ? Math.max(...rows.map((r) => r.views || 0)) : 1;
  return (
    <section
      className="rounded-md border border-black/10 bg-white p-5 sm:p-6"
      data-testid="analytics-countries"
    >
      <div className="flex items-center gap-2 text-brand-accent">
        <Globe size={14} strokeWidth={2.5} />
        <span className="label-kicker">Where they&apos;re reading from</span>
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-3">
        <h2 className="font-display text-lg font-black">Top countries</h2>
        {totalViews && tracked < totalViews ? (
          <span className="text-[11px] text-[var(--brand-body)]">
            {tracked.toLocaleString("en-GB")} of {totalViews.toLocaleString("en-GB")} views located
          </span>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-[var(--brand-body)]">
          No located visits yet. New page-views will start being tagged with a country immediately.
        </p>
      ) : (
        <ul className="mt-4 space-y-2" data-testid="top-countries">
          {rows.map((row) => {
            const code = row.country;
            const pct = Math.round((row.views / max) * 100);
            const pctOfTotal = totalViews ? Math.round((row.views / totalViews) * 100) : 0;
            return (
              <li
                key={code}
                className="relative overflow-hidden rounded-md border border-black/5 bg-[#F8FAFC] px-3 py-2"
                data-testid={`country-${code}`}
              >
                <div
                  className="absolute inset-y-0 left-0 bg-[#6CABDD]/15"
                  style={{ width: `${pct}%` }}
                />
                <div className="relative flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1 flex items-center gap-2">
                    <span className="text-base leading-none" aria-hidden="true">
                      {flagEmoji(code)}
                    </span>
                    <span className="truncate font-display text-sm font-bold">
                      {COUNTRY_NAMES[code] || code}
                    </span>
                    <span className="shrink-0 font-mono text-[10px] uppercase text-[var(--brand-body)]">
                      {code}
                    </span>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono text-xs text-[var(--brand-ink)]">
                      {row.views.toLocaleString("en-GB")}
                    </div>
                    {pctOfTotal > 0 ? (
                      <div className="font-mono text-[10px] text-[var(--brand-body)]">
                        {pctOfTotal}%
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}


// ---------- Excluded IPs ----------

function ExcludedIpsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ipInput, setIpInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/analytics/excluded-ips");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(formatApiErrorDetail(e) || "Failed to load excluded IPs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addCurrent = async () => {
    setError("");
    setBusy(true);
    try {
      const r = await api.get("/admin/analytics/my-ip");
      const ip = r.data?.ip || "";
      if (!ip) {
        setError("Couldn't read your current IP.");
      } else {
        await api.post("/admin/analytics/excluded-ips", { ip, note: "Me" });
        await load();
      }
    } catch (e) {
      setError(formatApiErrorDetail(e) || "Failed to add your current IP.");
    } finally {
      setBusy(false);
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!ipInput.trim()) return;
    setError("");
    setBusy(true);
    try {
      await api.post("/admin/analytics/excluded-ips", {
        ip: ipInput.trim(),
        note: noteInput.trim(),
      });
      setIpInput("");
      setNoteInput("");
      await load();
    } catch (e2) {
      setError(formatApiErrorDetail(e2) || "Failed to add IP.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ip) => {
    if (!window.confirm(`Stop excluding ${ip}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/analytics/excluded-ips/${encodeURIComponent(ip)}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e) || "Failed to remove.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="rounded-md border border-black/10 bg-white p-5 sm:p-6"
      data-testid="analytics-excluded-ips"
    >
      <div className="flex items-center gap-2 text-brand-accent">
        <Ban size={14} strokeWidth={2.5} />
        <span className="label-kicker">Ignore-list</span>
      </div>
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-display text-lg font-black">Excluded IPs</h2>
        <button
          type="button"
          onClick={addCurrent}
          disabled={busy}
          data-testid="excluded-ip-add-current"
          className="rounded-md border border-black/15 bg-white px-3 py-1.5 font-display text-xs font-semibold hover:border-[#0A0F17] disabled:opacity-50"
        >
          {busy ? "…" : "Exclude my current IP"}
        </button>
      </div>
      <p className="mt-1 text-xs text-[var(--brand-body)]">
        Visits from these IP addresses are silently dropped. Useful for filtering out your phone, family, work, etc.
      </p>

      <form
        onSubmit={addManual}
        className="mt-4 flex flex-wrap items-stretch gap-2"
        data-testid="excluded-ip-add-form"
      >
        <input
          value={ipInput}
          onChange={(e) => setIpInput(e.target.value)}
          placeholder="IPv4 or IPv6 address"
          data-testid="excluded-ip-add-ip"
          className="min-w-0 flex-1 rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]"
        />
        <input
          value={noteInput}
          onChange={(e) => setNoteInput(e.target.value)}
          placeholder="note (optional, e.g. 'Phone')"
          maxLength={80}
          data-testid="excluded-ip-add-note"
          className="min-w-0 flex-1 rounded-md border border-black/15 bg-white px-3 py-2 text-xs outline-none focus:border-[#0A0F17]"
        />
        <button
          type="submit"
          disabled={busy || !ipInput.trim()}
          data-testid="excluded-ip-add-submit"
          className="shrink-0 rounded-md bg-[#0A0F17] px-3 py-2 font-display text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          Add
        </button>
      </form>

      {error ? (
        <p className="mt-3 text-xs text-red-600" data-testid="excluded-ip-error">
          {error}
        </p>
      ) : null}

      <div className="mt-4 border-t border-black/5 pt-3">
        {loading ? (
          <p className="text-xs text-[var(--brand-body)]">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-[var(--brand-body)]">No IPs excluded yet.</p>
        ) : (
          <ul className="space-y-2" data-testid="excluded-ips-list">
            {rows.map((row) => (
              <li
                key={row.ip}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-black/5 bg-[#F8FAFC] px-3 py-2"
                data-testid={`excluded-ip-row-${row.ip}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="break-all font-mono text-xs text-[var(--brand-ink)]">
                    {row.ip}
                  </div>
                  {row.note ? (
                    <div className="mt-0.5 text-[11px] text-[var(--brand-body)]">
                      {row.note}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => remove(row.ip)}
                  disabled={busy}
                  data-testid={`excluded-ip-remove-${row.ip}`}
                  className="shrink-0 rounded-md border border-black/15 bg-white px-2 py-1 font-display text-[11px] font-semibold hover:border-red-500 hover:text-red-600 disabled:opacity-50"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

